import { onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { MessageCirclePlus, UserRound } from '@lucide/vue';
import { SparkleIcon } from '@blpde/nx-icons';
import { readShellAccount } from '@blpde/connect';
import { useNxCommands, type NxCommand } from '@blpde/nx-ui';
import type { ChatAccount } from '@/chat/chatApi';
import { appTranslator } from '@/i18n/appI18n';

/**
 * Palette entries of apps whose action is a shell panel (I-2348): „Neue
 * Nachricht an …“ opens the shell's DM panel with a colleague, „Assistent
 * öffnen“ the shell's assistant. They belong to the chat and agent apps (ids
 * and texts), but only the shell layout can open those panels — it registers
 * them through `useShellAppCommands`.
 */
export interface ShellAppCommandContext {
  /** Caption and search words of one app's entries, from its own table. */
  chatT: (key: string) => string;
  agentT: (key: string) => string;
  chatLabel: string;
  agentLabel: string;
  /** The account may use the app and its module is up (the FAB's gate). */
  canUseChat: () => boolean;
  canUseAssistant: () => boolean;
  /** The colleagues one can write to (not the caller). */
  colleagues: () => Promise<readonly ChatAccount[]>;
  openChatWith: (account: ChatAccount) => void;
  openAssistant: () => void;
}

export function buildShellAppCommands(ctx: ShellAppCommandContext): NxCommand[] {
  return [
    {
      id: 'chat.newDirectMessage',
      label: `${ctx.chatLabel}: ${ctx.chatT('chat.command.newDirectMessage')}`,
      group: ctx.chatLabel,
      app: 'chat',
      icon: MessageCirclePlus,
      category: 'action',
      keywords: ['chat', 'nachricht', 'message', 'direktnachricht', 'dm', 'schreiben', 'kollege'],
      when: ctx.canUseChat,
      run: () => {},
      children: async () =>
        (await ctx.colleagues()).map(
          (account): NxCommand => ({
            id: `chat.newDirectMessage.${account.id}`,
            label: account.name || account.email,
            hint: account.name ? account.email : undefined,
            icon: UserRound,
            app: 'chat',
            category: 'person',
            transient: true,
            run: () => ctx.openChatWith(account),
          }),
        ),
    },
    {
      id: 'agent.assistant',
      label: `${ctx.agentLabel}: ${ctx.agentT('agent.command.openAssistant')}`,
      group: ctx.agentLabel,
      app: 'agent',
      icon: SparkleIcon,
      category: 'action',
      keywords: ['assistent', 'assistant', 'ki', 'ai', 'agent', 'fragen', 'hilfe'],
      when: ctx.canUseAssistant,
      run: () => ctx.openAssistant(),
    },
  ];
}

/** Active colleagues other than the caller, by name — the DM panel's picker list. */
export function chatColleagues(accounts: readonly ChatAccount[]): ChatAccount[] {
  const me = readShellAccount();
  return accounts
    .filter((a) => a.active !== false)
    .filter((a) => !(me?.uuid && a.uuid === me.uuid) && !(me?.email && a.email === me.email))
    .slice()
    .sort((a, b) =>
      (a.name || a.email || '').localeCompare(b.name || b.email || '', 'de', {
        sensitivity: 'base',
      }),
    );
}

/**
 * Registers `buildShellAppCommands` for the shell layout's lifetime and again
 * when the language changes. Call in the shell layout's `setup()`.
 */
export function useShellAppCommands(
  deps: Pick<
    ShellAppCommandContext,
    'canUseChat' | 'canUseAssistant' | 'openChatWith' | 'openAssistant'
  >,
): void {
  const registry = useNxCommands();
  if (!registry) return;
  const { t, locale } = useI18n();
  let unregister: () => void = () => {};
  watch(
    () => locale.value,
    () => {
      unregister();
      unregister = registry.register(
        buildShellAppCommands({
          ...deps,
          chatT: appTranslator('chat'),
          agentT: appTranslator('agent'),
          chatLabel: t('connect.apps.chat'),
          agentLabel: t('connect.apps.agent'),
          colleagues: async () => {
            const { listChatAccounts } = await import('@/chat/chatApi');
            return chatColleagues(await listChatAccounts());
          },
        }),
      );
    },
    { immediate: true },
  );
  onBeforeUnmount(() => {
    unregister();
    unregister = () => {};
  });
}
